import React, { useMemo } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AdminStatusPill from '../../../components/admin/AdminStatusPill';
import AdminSurface from '../../../components/admin/AdminSurface';
import type { EmailPipelineAttachment, EmailPipelineDetailsDialogProps } from '../../../types/features/emailPipeline';
import { formatDateTimeMMDDYYYY } from '../../../utils/formatUtils';
import {
  formatEmailPipelineLogMessage,
  getEmailPipelineLogValue,
  isStructuredEmailPipelineLogMessage,
} from '../../../utils/formatters/emailPipelineFormatters';
import { getStatusLabel } from '../../../utils/formatters/statusFormatters';
import { isProcessedStatus, normalizeStatus } from '../../../utils/normalizers/statusNormalizers';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminIconActionButtonSx,
  adminInfoPanelSx,
  adminTableSx,
  adminTextLinkSx,
} from '../../../styles/adminComponentStyles';
import type { AdminSx } from '../../../styles/adminComponentStyles';
import { getEmailPipelineStatusTone } from '../mappers/emailPipelineFormatters';
import { useEmailPipelineAttachmentDownload } from '../hooks/useEmailPipelineAttachmentDownload';
import {
  getEmailPipelineAttachments,
  getEmailPipelineProcessingInstances,
} from '../mappers/emailPipelineNormalizers';
import { sanitizeEmailHtml } from '../sanitizers/emailHtmlSanitizer';

type DetailRow = [string, React.ReactNode];

const detailLabelSx = {
  ...adminTypography.helper,
  color: adminColors.mutedText,
  flexShrink: 0,
  fontWeight: 600,
  textTransform: 'uppercase',
  width: 94,
} as const;

const detailValueSx = {
  ...adminTypography.body,
  color: adminColors.text,
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
} as const;

const technicalDetailLabelSx = {
  ...adminTypography.body,
  color: '#808080',
  flexShrink: 0,
  fontWeight: 400,
  width: 94,
} as const;

const technicalDetailValueSx = {
  ...adminTypography.body,
  color: '#222B45',
  flex: 1,
  fontWeight: 600,
  minWidth: 0,
  overflowWrap: 'anywhere',
} as const;

const detailAttributesSx = {
  backgroundColor: adminColors.surfaceAlt,
  border: `1px solid ${adminColors.divider}`,
  borderRadius: '6px',
  color: adminColors.text,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  maxHeight: 320,
  overflow: 'auto',
  p: 1,
  width: '100%',
} as const;

const detailAttributeRowSx = {
  alignItems: 'flex-start',
  columnGap: 0.75,
  display: 'grid',
  gridTemplateColumns: { xs: '104px minmax(0, 1fr)', sm: '128px minmax(0, 1fr)' },
  rowGap: 0.25,
} as const;

const detailAttributeNameSx = {
  ...adminTypography.helper,
  color: adminColors.mutedText,
  fontWeight: 600,
  overflowWrap: 'anywhere',
  textTransform: 'uppercase',
} as const;

const detailAttributeValueSx = {
  ...adminTypography.body,
  color: adminColors.text,
  minWidth: 0,
  overflowWrap: 'anywhere',
} as const;

const detailNestedAttributesSx = {
  borderLeft: `1px solid ${adminColors.divider}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.35,
  ml: 0,
  pl: 0.75,
} as const;

const detailArrayListSx = {
  ...adminTypography.body,
  color: adminColors.text,
  m: 0,
  pl: 1.5,
  '& > li': {
    mb: 0.35,
  },
  '& > li:last-of-type': {
    mb: 0,
  },
} as const;

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const humanizeAttributeName = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .replace(/\bId\b/g, 'ID');

const formatScalarAttributeValue = (value: unknown): string => {
  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return String(value);
};

const renderDetailValue = (value: React.ReactNode, valueSx: AdminSx = detailValueSx) => {
  if (!value) {
    return <Typography sx={valueSx}>-</Typography>;
  }

  if (React.isValidElement(value)) {
    return <Box sx={valueSx}>{value}</Box>;
  }

  return <Typography sx={valueSx}>{value}</Typography>;
};

const renderDetailRows = (
  rows: DetailRow[],
  labelSx: AdminSx = detailLabelSx,
  valueSx: AdminSx = detailValueSx,
) => (
  <Stack spacing={0.75}>
    {rows.map(([label, value]) => (
      <Stack key={label} direction="row" spacing={1} alignItems="flex-start">
        <Typography sx={labelSx}>{label}</Typography>
        {renderDetailValue(value, valueSx)}
      </Stack>
    ))}
  </Stack>
);

const renderStructuredAttributeValue = (value: unknown): React.ReactNode => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Typography sx={detailAttributeValueSx}>-</Typography>;
    }

    return (
      <Box component="ol" sx={detailArrayListSx}>
        {value.map((item, index) => (
          <Box component="li" key={`item-${index}`}>
            {renderStructuredAttributeValue(item)}
          </Box>
        ))}
      </Box>
    );
  }

  if (isRecordValue(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return <Typography sx={detailAttributeValueSx}>-</Typography>;
    }

    return (
      <Box sx={detailNestedAttributesSx}>
        {entries.map(([key, entryValue]) => (
          <Box key={key} sx={detailAttributeRowSx}>
            <Typography sx={detailAttributeNameSx}>{humanizeAttributeName(key)}</Typography>
            <Box sx={{ minWidth: 0 }}>{renderStructuredAttributeValue(entryValue)}</Box>
          </Box>
        ))}
      </Box>
    );
  }

  return <Typography sx={detailAttributeValueSx}>{formatScalarAttributeValue(value)}</Typography>;
};

const renderStructuredAttributes = (value: unknown): React.ReactNode => {
  if (Array.isArray(value)) {
    return <Box sx={detailAttributesSx}>{renderStructuredAttributeValue(value)}</Box>;
  }

  if (!isRecordValue(value)) {
    return formatScalarAttributeValue(value);
  }

  const entries = Object.entries(value);

  if (entries.length === 0) {
    return '-';
  }

  return (
    <Box sx={detailAttributesSx}>
      {entries.map(([key, entryValue]) => (
        <Box key={key} sx={detailAttributeRowSx}>
          <Typography sx={detailAttributeNameSx}>{humanizeAttributeName(key)}</Typography>
          <Box sx={{ minWidth: 0 }}>{renderStructuredAttributeValue(entryValue)}</Box>
        </Box>
      ))}
    </Box>
  );
};

const renderResultValue = (value: unknown): React.ReactNode => {
  if (!isStructuredEmailPipelineLogMessage(value)) {
    return (
      <Box sx={detailAttributesSx}>
        <Typography sx={detailAttributeValueSx}>{formatEmailPipelineLogMessage(value)}</Typography>
      </Box>
    );
  }

  return renderStructuredAttributes(getEmailPipelineLogValue(value));
};

const EmailPipelineDetailsDialog: React.FC<EmailPipelineDetailsDialogProps> = ({
  model,
  actions,
}) => {
  const downloadAttachment = useEmailPipelineAttachmentDownload();
  const detailsRecord = model.detailsData?.result?.records?.[0];
  const detailsStatus = normalizeStatus(detailsRecord?.status);
  const detailsRecords = Array.isArray(model.detailsData?.result?.records) ? model.detailsData?.result?.records || [] : [];
  const isProcessed = isProcessedStatus(detailsStatus);
  const actionButtonLabel = isProcessed ? 'Force reprocess' : 'Reprocess';

  const attachments = getEmailPipelineAttachments(detailsRecord);
  const processingInstances = getEmailPipelineProcessingInstances(detailsRecords, detailsRecord?.message_id);
  const sanitizedEmailBody = useMemo(
    () => sanitizeEmailHtml(String(detailsRecord?.normalized_payload?.body || '')),
    [detailsRecord?.normalized_payload?.body],
  );

  return (
    <Dialog
      open={model.open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: adminCombineSx(adminDialogSx, {
          overflow: 'hidden',
          width: { xs: '96vw', lg: 1040 },
        }),
      }}
    >
      <Box
        sx={adminCombineSx(adminDialogTitleSx, {
          backgroundColor: 'rgb(239, 246, 255)',
          color: adminColors.primary,
          fontWeight: 600,
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.primary }}>
            Email Details
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {!!detailsRecord?.status && (
              <AdminStatusPill
                label={getStatusLabel(detailsRecord.status)}
                tone={getEmailPipelineStatusTone(detailsRecord.status)}
              />
            )}
            <IconButton onClick={actions.onClose} sx={adminIconActionButtonSx} aria-label="Close email details">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={adminCombineSx(adminDialogContentSx, {
          maxHeight: 'calc(84vh - 64px)',
          overflow: 'auto',
          p: '20px',
          pt: '20px !important',
        })}
      >
        {model.detailsActionError ? (
          <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 1.25 })}>
            {model.detailsActionError}
          </Alert>
        ) : null}
        {model.detailsActionSuccess ? (
          <Alert severity="success" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.success, { mb: 1.25 })}>
            {model.detailsActionSuccess}
          </Alert>
        ) : null}

        {model.detailsLoading ? (
          <Box py={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : model.detailsError ? (
          <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger)}>
            {model.detailsError}
          </Alert>
        ) : !detailsRecord ? (
          <Alert severity="info" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.info)}>
            No details available.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...adminTypography.cardTitle, color: adminColors.text }}>
                  {detailsRecord.store_name || (detailsRecord.store_key ? `Store ${detailsRecord.store_key}` : 'Unknown Store')}
                </Typography>
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }} noWrap>
                  {detailsRecord.subject || '-'}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={actions.onProcessAction}
                disabled={model.detailsActionLoading}
                sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
              >
                {model.detailsActionLoading ? 'Processing...' : actionButtonLabel}
              </Button>
            </Stack>

            <AdminSurface sx={{ p: 1.5 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  {renderDetailRows([
                    ['From', detailsRecord.from_email || '-'],
                    ['To', detailsRecord.to_email || '-'],
                    ['Received', formatDateTimeMMDDYYYY(detailsRecord.received_at, { withSeconds: true })],
                    ['Created', formatDateTimeMMDDYYYY(detailsRecord.created_at, { withSeconds: true })],
                    ['Updated', formatDateTimeMMDDYYYY(detailsRecord.updated_at, { withSeconds: true })],
                    ['Processed', formatDateTimeMMDDYYYY(detailsRecord.processed_at, { withSeconds: true })],
                  ], technicalDetailLabelSx, technicalDetailValueSx)}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={adminCombineSx(adminInfoPanelSx, { height: '100%' })}>
                    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.primary, mb: 1 }}>
                      Technical Details
                    </Typography>
                    {renderDetailRows([
                      ['Record ID', detailsRecord.id],
                      ['Message ID', detailsRecord.message_id || '-'],
                      ['Store ID', detailsRecord.store_id || '-'],
                      ['Store Key', detailsRecord.store_key || '-'],
                      ['History ID', detailsRecord.history_id || '-'],
                    ], technicalDetailLabelSx, technicalDetailValueSx)}
                  </Box>
                </Grid>
              </Grid>
            </AdminSurface>

            <Box>
              <Typography sx={{ ...adminTypography.sectionTitle, color: '#2C6CA0', mb: 0.75 }}>
                Result
              </Typography>
              {renderResultValue(detailsRecord.log_message)}
            </Box>

            <Box>
              <Typography sx={{ ...adminTypography.sectionTitle, color: '#2C6CA0', mb: 0.75 }}>
                Email Body
              </Typography>
              <AdminSurface
                sx={{
                  p: 1.5,
                  '& a': adminTextLinkSx,
                  '& p': { margin: 0, mb: 1 },
                }}
              >
                {sanitizedEmailBody ? (
                  <Box
                    sx={{ ...adminTypography.body, color: adminColors.textSoft }}
                    dangerouslySetInnerHTML={{ __html: sanitizedEmailBody }}
                  />
                ) : (
                  <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
                    No email body found.
                  </Typography>
                )}
              </AdminSurface>
            </Box>

            <Box>
              <Typography sx={{ ...adminTypography.sectionTitle, color: '#2C6CA0', mb: 0.75 }}>
                Attachments
              </Typography>
              <AdminSurface sx={{ p: 1.5 }}>
                {attachments.length === 0 ? (
                  <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, textAlign: 'center' }}>No attachments</Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {attachments.map((attachment: EmailPipelineAttachment, index: number) => {
                      const name = attachment?.filename || attachment?.name || attachment?.file_name || `Attachment ${index + 1}`;
                      const size = attachment?.size || attachment?.bytes;
                      const rawUrl = attachment?.path;
                      return (
                        <Typography key={`${name}-${index}`} sx={adminTypography.body}>
                          {rawUrl ? (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => { void downloadAttachment(String(rawUrl), name); }}
                              sx={adminCombineSx(adminTextLinkSx, {
                                background: 'transparent',
                                border: 0,
                                display: 'inline',
                                font: 'inherit',
                                p: 0,
                                textAlign: 'left',
                              })}
                            >
                              {name}
                            </Box>
                          ) : (
                            <Box component="span" sx={adminTextLinkSx}>
                              {name}
                            </Box>
                          )}
                          {size ? <Box component="span" sx={{ color: adminColors.text }}> ({size} KB)</Box> : null}
                        </Typography>
                      );
                    })}
                  </Stack>
                )}
              </AdminSurface>
            </Box>

            <Box>
              <Typography sx={{ ...adminTypography.sectionTitle, color: '#2C6CA0', mb: 0.75 }}>
                Processing Instances
              </Typography>
              <AdminSurface sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table
                    size="small"
                    sx={adminCombineSx(adminTableSx, {
                      minWidth: 760,
                      '& .MuiTableCell-root': {
                        padding: '8px 12px !important',
                      },
                    })}
                  >
                    <TableHead sx={{ '& th': { padding: '8px 12px !important', textTransform: 'uppercase' } }}>
                      <TableRow>
                        <TableCell>Attempt</TableCell>
                        <TableCell sx={{ width: 150 }}>Processed At</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Log Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processingInstances.map((instance, index: number) => (
                        <TableRow key={`instance-${index}`}>
                          <TableCell>{instance?.attempt ?? index + 1}</TableCell>
                          <TableCell>{formatDateTimeMMDDYYYY(instance?.processed_at, { withSeconds: true })}</TableCell>
                          <TableCell>
                            <AdminStatusPill
                              label={getStatusLabel(instance?.status)}
                              tone={getEmailPipelineStatusTone(instance?.status)}
                            />
                          </TableCell>
                          <TableCell sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                            {formatEmailPipelineLogMessage(instance?.log_message)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {processingInstances.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ borderBottom: 0 }}>
                            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, textAlign: 'center' }}>
                              No processing instances found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AdminSurface>
            </Box>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};

export default EmailPipelineDetailsDialog;
